import * as React from 'react';

// PACKAGES
import { Link } from 'react-router-dom';

// COMPONENTS
import Button from './Button';
import DisplayDateTime from './DisplayDateTime';
import DisplayDateTimeNoParse from './DisplayDateTimeNoParse';
import StatusIcon from './StatusIcon';
import LoadingSpinner from './LoadingSpinner';

// ICONS
import { ReactComponent as PlusIcon } from '../assets/svg/plus.svg';

// TYPES
import { StaffFetchChecklist } from '../api/models/staff-fetch-checklist';

interface ProfileChecklistsTypes {
	overdueChecklists?: Array<StaffFetchChecklist>;
	completedChecklists?: Array<StaffFetchChecklist>;
	overdueTotalCount?: number;
	completedTotalCount?: number;
	onLoadMoreOverdue?: () => void | Promise<void>;
	onLoadMoreCompleted?: () => void | Promise<void>;
	loadingMoreOverdue?: boolean;
	loadingMoreCompleted?: boolean;
	defaultMaxItems?: number;
	isPastProfile?: boolean;
}

const renderOverdueChecklistItem = (item: StaffFetchChecklist, index: number, isPastProfile?: boolean) => (
	<div key={item.id || index} className="grid-list-row">
		<div className="grid-list-row__inner">
			<div className="grid-cell-icon">
				<StatusIcon statusType="danger" />
			</div>

			<div className="grid-cell-name">{item.purpose}</div>

			<div className="grid-cell-created">
				<span className="title body-medium">Created by</span>

				<span className="name body-medium text-grey-4">{item.creator}</span>
			</div>

			<div className="grid-cell-date">
				<span className="title body-medium">Due date</span>

				<span className="status body-medium text-grey-4">
					<DisplayDateTimeNoParse format="D/M/YYYY, h:mm a">{item.dueDate}</DisplayDateTimeNoParse>
				</span>
			</div>

			<div className="grid-cell-action">
				<Link className="btn-text complaints-page" to={`/safety-checklists/${item.id}`}>
					View
				</Link>
			</div>
		</div>
	</div>
);

const renderCompletedChecklistItem = (item: StaffFetchChecklist, index: number, isPastProfile?: boolean) => (
	<div key={item.id || index} className="grid-list-row">
		<div className="grid-list-row__inner">
			<div className="grid-cell-icon">
				<StatusIcon statusType={item.status === 'Success' ? 'success' : 'warning'} />
			</div>

			<div className="grid-cell-name">{item.purpose}</div>

			<div className="grid-cell-created">
				<span className="title body-medium">Created by</span>

				<span className="name body-medium text-grey-4">{item.creator}</span>
			</div>

			<div className="grid-cell-date">
				<span className="title body-medium">Date</span>

				<span className="status body-medium text-grey-4">
					<DisplayDateTime format="D/M/YYYY, h:mm a">{item.updated}</DisplayDateTime>
				</span>
			</div>

			<div className="grid-cell-action">
				<Link className="btn-text complaints-page" to={`/safety-checklists/${item.id}`}>
					View
				</Link>
			</div>
		</div>
	</div>
);

const ProfileChecklists: React.FC<ProfileChecklistsTypes> = ({
	overdueChecklists,
	completedChecklists,
	overdueTotalCount = 0,
	completedTotalCount = 0,
	onLoadMoreOverdue,
	onLoadMoreCompleted,
	loadingMoreOverdue = false,
	loadingMoreCompleted = false,
	defaultMaxItems = 10,
	isPastProfile
}) => {
	const hasOverdue = overdueChecklists && overdueChecklists.length > 0;

	const hasCompleted = completedChecklists && completedChecklists.length > 0;

	const showOverdueMore =
		hasOverdue && (overdueTotalCount ?? 0) > (overdueChecklists?.length ?? 0) && onLoadMoreOverdue;

	const showCompletedMore =
		hasCompleted && (completedTotalCount ?? 0) > (completedChecklists?.length ?? 0) && onLoadMoreCompleted;

	if (!hasOverdue && !hasCompleted) {
		return null;
	}

	return (
		<section className="info-section profile-checklists">
			<h4 className="fw-bold mbtom-16 text-grey-5">Safety Checklists</h4>

			{hasOverdue && overdueChecklists && (
				<>
					<h5 className="section-header bold body-normal mb-0">Overdue</h5>

					<div className="grid-list">
						{overdueChecklists.map((item, index) => renderOverdueChecklistItem(item, index, isPastProfile))}
					</div>

					{showOverdueMore && (
						<Button
							className="btn-secondary btn-show-more"
							onClick={onLoadMoreOverdue}
							disabled={loadingMoreOverdue}
							buttonIcon={
								loadingMoreOverdue ? (
									<span className="btn-show-more-loading">
										<LoadingSpinner show={true} isStatic width={18} height={18} />
										Loading...
									</span>
								) : (
									<PlusIcon className="link-icon" />
								)
							}
						>
							{!loadingMoreOverdue &&
								`Show ${Math.min(defaultMaxItems, (overdueTotalCount ?? 0) - (overdueChecklists?.length ?? 0))} more`}
						</Button>
					)}
				</>
			)}

			{hasCompleted && completedChecklists && (
				<>
					<h5 className={`section-header bold body-normal mb-0 ${hasOverdue ? 'mtop-14' : ''}`}>Completed</h5>

					<div className="grid-list">
						{completedChecklists.map((item, index) => renderCompletedChecklistItem(item, index, isPastProfile))}
					</div>

					{showCompletedMore && (
						<Button
							className="btn-secondary btn-show-more"
							onClick={onLoadMoreCompleted}
							disabled={loadingMoreCompleted}
							buttonIcon={
								loadingMoreCompleted ? (
									<span className="btn-show-more-loading">
										<LoadingSpinner show={true} isStatic width={18} height={18} />
										Loading...
									</span>
								) : (
									<PlusIcon className="link-icon" />
								)
							}
						>
							{!loadingMoreCompleted &&
								`Show ${Math.min(
									defaultMaxItems,
									(completedTotalCount ?? 0) - (completedChecklists?.length ?? 0)
								)} more`}
						</Button>
					)}
				</>
			)}
		</section>
	);
};

export default ProfileChecklists;
